/**
 * Topography TIN engine (topo round — docs/MODELER.md "TOPOGRAPHY element").
 *
 * MESH (TIN), not NURBS: the designer's spot points ARE the intent, a Delaunay TIN
 * over them is the industry representation, and everything else here DERIVES from it.
 * Delaunay comes from `delaunator` (Mapbox — robust-predicates incircle, battle-
 * tested; reliability is this round's acceptance criterion). Breaklines constrain the
 * triangulation by DENSIFICATION: intermediate vertices are inserted along each
 * breakline segment (z lerped), so triangulation edges follow the line to within the
 * densify step — the pragmatic conforming-Delaunay approach, with no CDT edge-flip
 * code to get wrong.
 *
 * GRADING is evaluated as a TOTAL FUNCTION over the plan: existing TIN z, then each
 * live modifier in stack order (later wins where footprints overlap). A DRAWN pad
 * holds its elevation inside the footprint and ties back to the surface at its
 * daylight slope outside it until the planes meet (min/max composition — no case can
 * fail). A REFERENCING pad (`refElementId` → a floor) instead goes VERTICAL at its
 * footprint boundary (terrain grading simplification round) — no daylight skirt at
 * all; the firm's practice is a straight cut/fill + retaining elements unless
 * feathering is a deliberate hand-drawn choice, so "grade to this slab" reuses the
 * retaining-edge crisp-cliff treatment instead of auto-feathering. A retaining
 * modifier blocks skirts from crossing its wall line, so the surface steps vertically
 * there instead of sloping. FOOTPRINTS BEAT SKIRTS (topo grading fix round, unchanged
 * by the simplification above): inside ANY live pad's footprint — drawn or
 * referencing — that pad's elevation is authoritative over every OTHER modifier's
 * daylight skirt regardless of stack order — stack order still resolves pad-vs-pad
 * footprint overlaps — and a skirt that would cross another pad's footprint
 * TERMINATES at the boundary with a vertical face; skirts feather only across free
 * terrain.
 *
 * The PROPOSED surface (TERRAIN LOCAL SURGERY round, 2026-08-15): referencing pads
 * and retaining lines are TRUE-EDGE LOCAL SURGERY on the mesh (surgery.ts — project
 * the boundary, split crossed facets so it becomes true mesh edges, delete the void,
 * stitch a vertical band); with only those live, every triangle outside the surgery
 * is BYTE-IDENTICAL to the existing TIN, which is what makes the existing/proposed
 * contour overlay coincide exactly where nothing changed. DRAWN grading (pads/grade
 * points/grade lines — intentional daylight skirts) keeps the recorded global path:
 * re-triangulation from existing vertices + densified drawn boundaries + a bounded
 * sample grid, every vertex's z evaluated through the total function. Errors are
 * impossible by construction: no booleans, no surface-to-surface intersections,
 * just evaluation plus local splits.
 */
import Delaunator from 'delaunator'
import type { PhaseRecord, Pt2, ResolvedElement, TopoBreakline, TopoModifier, TopoPoint } from '@aj/bim-kernel'
import { phaseStatus, phaseVisible, isFirstPhaseView, ceilingSlopePlane } from '@aj/bim-kernel'
import { flattenPath } from '../../flatten'
import { resolveSeams, splitTinAlongPath, splitTinAlongPaths } from './surgery'

export interface TopoTin {
  /** xy per vertex */
  pts: Pt2[]
  /** z per vertex, project-relative feet */
  zs: number[]
  /** triangle vertex indices, triples */
  tris: number[]
  /** convex hull ring as vertex indices, counter-clockwise */
  hull: number[]
}

const DENSIFY_FT = 2

// ---------- element param reads ----------

export function topoPointsOf(el: ResolvedElement): TopoPoint[] {
  const v = el.params.points
  return Array.isArray(v) ? (v as TopoPoint[]) : []
}
export function topoBreaklinesOf(el: ResolvedElement): TopoBreakline[] {
  const v = el.params.breaklines
  return Array.isArray(v) ? (v as TopoBreakline[]) : []
}
export function topoModifiersOf(el: ResolvedElement): TopoModifier[] {
  const v = el.params.modifiers
  return Array.isArray(v) ? (v as TopoModifier[]) : []
}

// ---------- small geometry helpers ----------

export function pointInRing(pt: Pt2, ring: Pt2[]): boolean {
  let inside = false
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i], [xj, yj] = ring[j]
    if ((yi > pt[1]) !== (yj > pt[1]) &&
        pt[0] < ((xj - xi) * (pt[1] - yi)) / (yj - yi) + xi) inside = !inside
  }
  return inside
}

/** distance from `pt` to the ring's boundary + the closest boundary point */
function distToRing(pt: Pt2, ring: Pt2[]): { d: number; at: Pt2 } {
  let best = Infinity
  let at: Pt2 = ring[0]
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const a = ring[j], b = ring[i]
    const dx = b[0] - a[0], dy = b[1] - a[1]
    const len2 = dx * dx + dy * dy
    const t = len2 > 1e-12 ? Math.max(0, Math.min(1, ((pt[0] - a[0]) * dx + (pt[1] - a[1]) * dy) / len2)) : 0
    const px = a[0] + dx * t, py = a[1] + dy * t
    const d = Math.hypot(pt[0] - px, pt[1] - py)
    if (d < best) { best = d; at = [px, py] }
  }
  return { d: best, at }
}

/**
 * Distance from `pt` to an OPEN polyline + the closest point + the interpolated
 * per-vertex value there (gradeLine's sloping elevation). `vals` absent → `at`/`d`
 * only, `val` = null.
 */
function distToPolyline(pt: Pt2, line: Pt2[], vals: number[] | null): { d: number; at: Pt2; val: number | null } {
  let best = Infinity
  let at: Pt2 = line[0]
  let val: number | null = vals ? vals[0] : null
  for (let i = 0; i + 1 < line.length; i++) {
    const a = line[i], b = line[i + 1]
    const dx = b[0] - a[0], dy = b[1] - a[1]
    const len2 = dx * dx + dy * dy
    const t = len2 > 1e-12 ? Math.max(0, Math.min(1, ((pt[0] - a[0]) * dx + (pt[1] - a[1]) * dy) / len2)) : 0
    const px = a[0] + dx * t, py = a[1] + dy * t
    const d = Math.hypot(pt[0] - px, pt[1] - py)
    if (d < best) {
      best = d
      at = [px, py]
      if (vals) val = vals[i] + (vals[i + 1] - vals[i]) * t
    }
  }
  return { d: best, at, val }
}

function segsIntersect(a: Pt2, b: Pt2, c: Pt2, d: Pt2): boolean {
  const o = (p: Pt2, q: Pt2, r: Pt2) => (q[0] - p[0]) * (r[1] - p[1]) - (q[1] - p[1]) * (r[0] - p[0])
  const o1 = o(a, b, c), o2 = o(a, b, d), o3 = o(c, d, a), o4 = o(c, d, b)
  return o1 * o2 < 0 && o3 * o4 < 0
}

export function ringArea(ring: Pt2[]): number {
  let s = 0
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    s += (ring[j][0] + ring[i][0]) * (ring[j][1] - ring[i][1])
  }
  return Math.abs(s) / 2
}

// ---------- existing TIN ----------

// ---------- terrain result caches (terrain perf round, 2026-08-15) ----------
// Every consumer (plan re-cuts, 3D solids, sections' existing-tin builds)
// re-derived identical tins on every edit — moving a chair paid a full seam
// surgery in the plan's terrain pass. Results cache by CONTENT fingerprint:
// unrelated edits hit; terrain-affecting edits rebuild exactly once and every
// other consumer then shares the same object (which also stabilizes identity
// for downstream identity-keyed caches like the pipeline's cut cache). Cached
// tins are treated as immutable everywhere — consumers read, surgery copies.

/** order-sensitive numeric hash over floats (1e-4 ft quantization) */
function mixNum(h: number, v: number): number {
  return (Math.imul(h, 31) + (Math.round(v * 1e4) | 0)) | 0
}
function mixStr(h: number, s: string): number {
  for (let i = 0; i < s.length; i++) h = (Math.imul(h, 31) + s.charCodeAt(i)) | 0
  return h
}
function mixPts(h: number, pts: Pt2[] | null | undefined): number {
  if (!pts) return mixNum(h, -1)
  h = mixNum(h, pts.length)
  for (const [x, y] of pts) { h = mixNum(h, x); h = mixNum(h, y) }
  return h
}

function fpExisting(points: TopoPoint[], breaklines: TopoBreakline[]): string {
  let h = 17
  h = mixNum(h, points.length)
  for (const p of points) { h = mixNum(h, p.pt[0]); h = mixNum(h, p.pt[1]); h = mixNum(h, p.zFt) }
  let h2 = 23
  h2 = mixNum(h2, breaklines.length)
  for (const bl of breaklines) for (const id of bl.ptIds) h2 = mixStr(h2, id)
  return `${points.length}:${h}:${h2}`
}

function fpModifiers(set: ResolvedTopoModifier[]): string {
  let h = 29
  h = mixNum(h, set.length)
  for (const r of set) {
    h = mixStr(h, r.mod.kind)
    h = mixNum(h, r.elevFt ?? -99999)
    if (r.plane) { h = mixNum(h, r.plane.m); h = mixPts(h, [r.plane.a, r.plane.n]) }
    h = mixNum(h, r.slopeH)
    h = mixNum(h, r.isReferencing ? 1 : 0)
    h = mixStr(h, r.phaseCreated ?? '')
    h = mixPts(h, r.poly)
    h = mixPts(h, r.line)
    h = mixPts(h, r.pt ? [r.pt] : null)
    if (r.zs) for (const z of r.zs) h = mixNum(h, z)
  }
  return `${set.length}:${h}`
}

function fpCutters(cutters: TerrainCutter[]): string {
  let h = 37
  h = mixNum(h, cutters.length)
  for (const c of cutters) {
    h = mixNum(h, c.zBot)
    h = mixNum(h, c.zTop)
    h = mixNum(h, c.unroofed ? 1 : 0)
    for (const r of c.rings) h = mixPts(h, r)
  }
  return `${cutters.length}:${h}`
}

/** small string-keyed LRU (Map preserves insertion order; re-set on hit) */
class Lru<V> {
  private m = new Map<string, V>()
  constructor(private cap: number) {}
  get(k: string): V | undefined {
    const v = this.m.get(k)
    if (v !== undefined) { this.m.delete(k); this.m.set(k, v) }
    return v
  }
  set(k: string, v: V): void {
    if (this.m.has(k)) this.m.delete(k)
    else if (this.m.size >= this.cap) this.m.delete(this.m.keys().next().value!)
    this.m.set(k, v)
  }
}

const EXISTING_CACHE = new Lru<TopoTin | null>(4)
const GRADED_CACHE = new WeakMap<TopoTin, Lru<TopoTin>>()

/**
 * Delaunay TIN over the spot points, breaklines densified in. Returns null below 3
 * usable points (a surface needs a triangle) — callers render points-only then.
 * Content-cached: identical survey data returns the SAME tin object.
 */
export function buildExistingTin(points: TopoPoint[], breaklines: TopoBreakline[]): TopoTin | null {
  const key = fpExisting(points, breaklines)
  const hit = EXISTING_CACHE.get(key)
  if (hit !== undefined) return hit
  const built = buildExistingTinImpl(points, breaklines)
  EXISTING_CACHE.set(key, built)
  return built
}

function buildExistingTinImpl(points: TopoPoint[], breaklines: TopoBreakline[]): TopoTin | null {
  if (points.length < 3) return null
  const byId = new Map(points.map((p) => [p.id, p]))
  const pts: Pt2[] = []
  const zs: number[] = []
  const seen = new Set<string>()
  const push = (x: number, y: number, z: number) => {
    const key = `${Math.round(x * 1e4)},${Math.round(y * 1e4)}`
    if (seen.has(key)) return
    seen.add(key)
    pts.push([x, y])
    zs.push(z)
  }
  for (const p of points) push(p.pt[0], p.pt[1], p.zFt)
  for (const bl of breaklines) {
    for (let i = 0; i + 1 < bl.ptIds.length; i++) {
      const a = byId.get(bl.ptIds[i]), b = byId.get(bl.ptIds[i + 1])
      if (!a || !b) continue
      const len = Math.hypot(b.pt[0] - a.pt[0], b.pt[1] - a.pt[1])
      const n = Math.min(32, Math.floor(len / DENSIFY_FT))
      for (let k = 1; k < n; k++) {
        const t = k / n
        push(
          a.pt[0] + (b.pt[0] - a.pt[0]) * t,
          a.pt[1] + (b.pt[1] - a.pt[1]) * t,
          a.zFt + (b.zFt - a.zFt) * t,
        )
      }
    }
  }
  if (pts.length < 3) return null
  const del = Delaunator.from(pts, (p: Pt2) => p[0], (p: Pt2) => p[1])
  if (!del.triangles.length) return null
  return { pts, zs, tris: Array.from(del.triangles), hull: Array.from(del.hull) }
}

/** barycentric z on the TIN at a plan point, or null outside the hull */
export function tinZAt(tin: TopoTin, pt: Pt2): number | null {
  const EPS = 1e-9
  const { pts, zs, tris } = tin
  for (let i = 0; i + 2 < tris.length; i += 3) {
    const a = pts[tris[i]], b = pts[tris[i + 1]], c = pts[tris[i + 2]]
    const minX = Math.min(a[0], b[0], c[0]), maxX = Math.max(a[0], b[0], c[0])
    if (pt[0] < minX - EPS || pt[0] > maxX + EPS) continue
    const minY = Math.min(a[1], b[1], c[1]), maxY = Math.max(a[1], b[1], c[1])
    if (pt[1] < minY - EPS || pt[1] > maxY + EPS) continue
    const denom = (b[1] - c[1]) * (a[0] - c[0]) + (c[0] - b[0]) * (a[1] - c[1])
    if (Math.abs(denom) < EPS) continue
    const w0 = ((b[1] - c[1]) * (pt[0] - c[0]) + (c[0] - b[0]) * (pt[1] - c[1])) / denom
    if (w0 < -1e-7 || w0 > 1 + 1e-7) continue
    const w1 = ((c[1] - a[1]) * (pt[0] - c[0]) + (a[0] - c[0]) * (pt[1] - c[1])) / denom
    if (w1 < -1e-7 || w1 > 1 + 1e-7) continue
    const w2 = 1 - w0 - w1
    if (w2 < -1e-7) continue
    return w0 * zs[tris[i]] + w1 * zs[tris[i + 1]] + w2 * zs[tris[i + 2]]
  }
  return null
}

export function hullRing(tin: TopoTin): Pt2[] {
  return tin.hull.map((i) => tin.pts[i])
}

// ---------- modifiers ----------

export interface ResolvedTopoModifier {
  mod: TopoModifier
  /** footprint ring (pad/region, drawn or derived) · gradeLine: OPEN polyline */
  poly: Pt2[] | null
  /** pad/grade/gradeLine: elevation, drawn or derived */
  elevFt: number | null
  /** pad/grade/gradeLine: effective daylight slope (instance override or type default) */
  slopeH: number
  /** retaining only: the referenced wall's line */
  line: Pt2[] | null
  /** grade only: the pinned spot */
  pt: Pt2 | null
  /** gradeLine only: validated per-vertex elevations (null = constant elevFt) */
  zs: number[] | null
  /**
   * pad only: resolved via `refElementId` (a floor's live footprint + top), as
   * opposed to a drawn footprint. Referencing pads grade VERTICALLY at their
   * boundary (terrain grading simplification round) — `slopeH` is read but never
   * applied for one; always false for every other kind.
   */
  isReferencing: boolean
  /**
   * SLOPED SLAB GRADING (Matt, 2026-10-08: "when a slab is set to grade the topo
   * terrain, if the slab is sloped, the graded region shows as flat under it"): a
   * referencing pad whose floor is SLOPED carries the floor's tilted plane — `elevFt`
   * is then the pad's height at the slope edge and `padZAt` adds the rise; absent =
   * a flat pad at `elevFt`, exactly as before.
   */
  plane?: { a: Pt2; n: Pt2; m: number } | null
  /**
   * PAVING HEAL (2026-10-12, Matt on Foulger: "coming out clean and healing the terrain model
   * underneath"): a pad DERIVED from a paving run (never stored — registerTerrainPadSource).
   * `zFn` is the run's UNDERSIDE at a point (padZAt reads it — the earth under the slab);
   * `edgeFn` its TOP, which the ground just outside the footprint meets (gradedZAt's edge
   * band), so the existing ground runs up to the slab edge flush and the facets between it and
   * the next survey vertex feather back — no gap, no grass through the concrete.
   */
  zFn?: (pt: Pt2) => number
  edgeFn?: (pt: Pt2) => number
  fromPaving?: boolean
  /**
   * The EFFECTIVE phase for status derivation (phase consolidation round): a
   * REFERENCING modifier inherits its referenced element's `phaseCreated` — grading
   * to an existing slab IS existing grading, whatever view the modifier happened to
   * be added from (Matt's report: an existing-condition slab set to grade read as a
   * new move). A drawn modifier keeps its own drawing-time stamp. Null = untagged
   * (= the first phase, per phasing.ts).
   */
  phaseCreated: string | null
}

/**
 * Resolve each modifier's live geometry. A REFERENCING pad reads its floor/slab's
 * footprint and top elevation at call time (level elevation + offset — the slab
 * generator's own formula), so it re-derives whenever the element moves; a dangling
 * reference resolves to nothing and the modifier is a visible no-op, never an error.
 */
/**
 * PAVING HEAL: categories that heal the terrain around themselves register a source here (the
 * paving geometry module does, on load) — keeps the import one-directional (paving reads the
 * EXISTING tin from this module; this module never imports paving).
 */
type TerrainPadSource = (byId: Map<string, ResolvedElement>) => ResolvedTopoModifier[]
const PAD_SOURCES: TerrainPadSource[] = []
export function registerTerrainPadSource(fn: TerrainPadSource): void {
  if (!PAD_SOURCES.includes(fn)) PAD_SOURCES.push(fn)
}

export function resolveTopoModifiers(
  el: ResolvedElement,
  byId?: Map<string, ResolvedElement>,
  mods?: TopoModifier[],
): ResolvedTopoModifier[] {
  const out = resolveStoredTopoModifiers(el, byId, mods)
  // derived heal pads go FIRST in the stack, so anything the designer drew on top still wins
  if (byId && !mods && PAD_SOURCES.length) {
    const derived: ResolvedTopoModifier[] = []
    for (const src of PAD_SOURCES) {
      try { derived.push(...src(byId)) } catch { /* a source that can't answer heals nothing */ }
    }
    if (derived.length) return [...derived, ...out]
  }
  return out
}

function resolveStoredTopoModifiers(
  el: ResolvedElement,
  byId?: Map<string, ResolvedElement>,
  mods?: TopoModifier[],
): ResolvedTopoModifier[] {
  const defaultSlope = typeof el.params.defaultSlopeH === 'number' && el.params.defaultSlopeH > 0
    ? (el.params.defaultSlopeH as number) : 2
  const out: ResolvedTopoModifier[] = []
  for (const mod of mods ?? topoModifiersOf(el)) {
    const slopeH = typeof mod.slopeH === 'number' && mod.slopeH > 0 ? mod.slopeH : defaultSlope
    // effective phase: a resolving REFERENCE inherits the referenced element's own
    // phaseCreated (untagged ref = null = first phase — never fall back to the
    // modifier's stamp, that would resurrect the existing-slab-reads-as-new-move
    // bug); drawn modifiers keep their drawing-time stamp
    const refEl = mod.refElementId && byId ? byId.get(mod.refElementId) : null
    const phaseCreated = refEl
      ? ((refEl.phaseCreated as string | undefined) ?? null)
      : ((mod.phaseCreated as string | undefined) ?? null)
    if (mod.kind === 'pad') {
      let poly: Pt2[] | null = mod.poly && mod.poly.length >= 3 ? mod.poly : null
      let elevFt: number | null = typeof mod.elevFt === 'number' ? mod.elevFt : null
      let isReferencing = false
      let plane: ResolvedTopoModifier['plane'] = null
      if (mod.refElementId && byId) {
        // FLOOR references only (review fix: the top formula below is the floor
        // slab's — a ceiling's top adds thickness, a roof's boundary is a bearing
        // line; a wrong-category ref resolves to nothing, a visible no-op)
        const ref = byId.get(mod.refElementId)
        const boundary = ref?.params.boundary as import('@aj/bim-kernel').Path2D | null
        if (ref && ref.category === 'floor' && ref.level && boundary && boundary.closed && boundary.nodes.length >= 3) {
          const ring = flattenPath(boundary)
          if (ring.length >= 3) {
            poly = ring
            isReferencing = true
            // grade to the slab's UNDERSIDE — the subgrade the concrete bears on
            // (Matt's report: grading to the TOP left the terrain coplanar with the
            // slab's own top face — a z-fighting shimmer — and earth over a slab is
            // wrong construction anyway; the slab edge now reads proud of grade).
            // Floor tops sit at level + offset with thickness stacking DOWN (the
            // slab generator's own formula); a thickness-less floor falls back to a
            // hair below its top so the two faces still never coincide.
            const top = ref.level.elevationFt + ((ref.params.heightOffsetFt as number) || 0)
            // SLOPED SLAB GRADING: a sloped floor's top tilts from its slope edge (the
            // slab generator's own plane, ceilingSlopePlane) and its thickness runs
            // plumb — so the subgrade under it tilts with it, `t·√(1+m²)` below the top
            const sp = ceilingSlopePlane(boundary)
            const t = ref.assemblyThicknessFt > 0 ? ref.assemblyThicknessFt : 0.05
            if (sp) {
              plane = { a: [sp.a[0], sp.a[1]], n: [sp.n[0], sp.n[1]], m: sp.m }
              elevFt = top - t * Math.sqrt(1 + sp.m * sp.m)
            } else {
              elevFt = top - t
            }
          }
        }
      }
      out.push({ mod, poly, elevFt, slopeH, line: null, pt: null, zs: null, phaseCreated, isReferencing, plane })
    } else if (mod.kind === 'grade') {
      // manual-grading round: a grade POINT pins the surface to an elevation at one
      // spot and daylights back at the slope — the pad-edge math, point case
      const pt: Pt2 | null = Array.isArray(mod.pt) && mod.pt.length === 2 ? [mod.pt[0], mod.pt[1]] : null
      const elevFt = typeof mod.elevFt === 'number' ? mod.elevFt : null
      out.push({ mod, poly: null, elevFt, slopeH, line: null, pt, zs: null, phaseCreated, isReferencing: false })
    } else if (mod.kind === 'gradeLine') {
      // …and the polyline case (swale invert, berm ridge, driveway crown) — OPEN,
      // constant elevation or per-vertex zs (validated: wrong length = constant)
      const poly = Array.isArray(mod.poly) && mod.poly.length >= 2 ? mod.poly : null
      const elevFt = typeof mod.elevFt === 'number' ? mod.elevFt : null
      const zs = poly && Array.isArray(mod.zs) && mod.zs.length === poly.length
        && mod.zs.every((z) => typeof z === 'number') ? mod.zs : null
      out.push({ mod, poly, elevFt, slopeH, line: null, pt: null, zs, phaseCreated, isReferencing: false })
    } else if (mod.kind === 'retaining') {
      let line: Pt2[] | null = null
      if (mod.refElementId && byId) {
        // WALL references only; flattened so arc segments follow the true curve
        // (the STORED reference path, not the offset centerline — close enough for
        // a skirt blocker, recorded as a v1 simplification)
        const ref = byId.get(mod.refElementId)
        const path = ref?.params.path as import('@aj/bim-kernel').Path2D | null
        if (ref && ref.category === 'wall' && path && Array.isArray(path.nodes) && path.nodes.length >= 2) {
          const pts = flattenPath(path)
          if (pts.length >= 2) {
            line = pts
            if (path.closed) line.push([pts[0][0], pts[0][1]])
          }
        }
      }
      out.push({ mod, poly: null, elevFt: null, slopeH, line, pt: null, zs: null, phaseCreated, isReferencing: false })
    } else {
      out.push({ mod, poly: mod.poly && mod.poly.length >= 3 ? mod.poly : null, elevFt: null, slopeH, line: null, pt: null, zs: null, phaseCreated, isReferencing: false })
    }
  }
  return out
}

/**
 * The modifiers LIVE at a view's phase, honoring the view's phaseFilter exactly like
 * elements do (phase consolidation round): 'future' is out everywhere but overlay,
 * and the 'demo'/'existing' filters exclude new-this-phase modifiers so those views
 * show the pre-work terrain. Absent viewPhase (a model with no phases, or the 3D
 * scene) applies everything. Status derives from the modifier's EFFECTIVE phase
 * (`r.phaseCreated` — referencing modifiers inherit their element's phase); an
 * untagged modifier reads as first-phase (existing) per phasing.ts.
 */
export function liveTopoModifiers(
  resolved: ResolvedTopoModifier[],
  phases: PhaseRecord[],
  viewPhase: string | null | undefined,
  phaseFilter?: string | null,
): ResolvedTopoModifier[] {
  if (!viewPhase) return resolved
  const filter = (phaseFilter ?? 'asBuilt') as Parameters<typeof phaseVisible>[0]
  const isFirst = isFirstPhaseView(phases, viewPhase as never)
  return resolved.filter((r) => {
    const st = phaseStatus(phases, { phaseCreated: r.phaseCreated as never }, viewPhase as never)
    return st === null ? true : phaseVisible(filter, st, isFirst)
  })
}

/**
 * The modifiers that PRE-EXIST a view's phase (status 'existing') — the baseline
 * surface for the existing-vs-proposed contour overlay: grading that happened in an
 * earlier phase (an existing slab's feather) belongs to the EXISTING contours; only
 * this phase's own grading reads as a proposed move (Matt's report, 2026-08-12).
 */
export function existingTopoModifiers(
  resolved: ResolvedTopoModifier[],
  phases: PhaseRecord[],
  viewPhase: string | null | undefined,
): ResolvedTopoModifier[] {
  if (!viewPhase) return []
  return resolved.filter((r) =>
    phaseStatus(phases, { phaseCreated: r.phaseCreated as never }, viewPhase as never) === 'existing')
}

/** how near a blocker may sit to the grading geometry's nearest point and still
 *  block — covers a retaining line coincident with (or a wall-thickness inside)
 *  the pad edge. This used to be a 1-ft segment OVERSHOOT past `at`, but that made
 *  blocking DIRECTION-dependent: when `at` is a ring CORNER, the overshoot sweeps
 *  across whatever walls meet there as the sample point moves, flipping blocked ⇄
 *  unblocked between samples 0.04' apart — the Forsyth spike field, made visible
 *  the moment adaptive densification started sampling those zones finely. Distance
 *  from the blocker to `at` is the rotation-invariant version of the same intent. */
const RETAIN_REACH_FT = 1

/** distance from segment ab to point p */
function segDistToPoint(a: Pt2, b: Pt2, p: Pt2): number {
  const dx = b[0] - a[0], dy = b[1] - a[1]
  const len2 = dx * dx + dy * dy
  const t = len2 > 1e-12 ? Math.max(0, Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / len2)) : 0
  return Math.hypot(p[0] - (a[0] + dx * t), p[1] - (a[1] + dy * t))
}

interface LiveCaches {
  retainLines: Pt2[][]
  /** live pads with a footprint + elevation, in stack order, bbox for quick reject */
  pads: { r: ResolvedTopoModifier; bbox: [number, number, number, number] }[]
}
const LIVE_CACHE = new WeakMap<object, LiveCaches>()

/** per-live-array caches (review fix lineage: cutFill alone evaluates gradedZAt
 *  tens of thousands of times per call — a fresh filter/map per sample was waste) */
function liveCaches(live: ResolvedTopoModifier[]): LiveCaches {
  let c = LIVE_CACHE.get(live)
  if (!c) {
    const retainLines = live.filter((r) => r.mod.kind === 'retaining' && r.line).map((r) => r.line!)
    const pads = live
      .filter((r) => r.mod.kind === 'pad' && r.poly && r.poly.length >= 3 && r.elevFt !== null)
      .map((r) => {
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
        for (const [x, y] of r.poly!) {
          if (x < minX) minX = x
          if (x > maxX) maxX = x
          if (y < minY) minY = y
          if (y > maxY) maxY = y
        }
        return { r, bbox: [minX, minY, maxX, maxY] as [number, number, number, number] }
      })
    c = { retainLines, pads }
    LIVE_CACHE.set(live, c)
  }
  return c
}

/** PAVING HEAL: how far outside a paving run the ground is held to the slab's top edge — just
 *  past the seam sampler's 0.02', well inside any survey spacing */
const PAVING_EDGE_BAND = 0.1

/** a pad's elevation at a plan point — flat, or on its sloped floor's plane */
export function padZAt(r: ResolvedTopoModifier, pt: Pt2): number {
  if (r.zFn) return r.zFn(pt)
  const z = r.elevFt as number
  const pl = r.plane
  return pl ? z + pl.m * ((pt[0] - pl.a[0]) * pl.n[0] + (pt[1] - pl.a[1]) * pl.n[1]) : z
}

/**
 * The graded surface as a total function: existing TIN z composed through the live
 * pad/retaining modifiers in stack order. Returns null outside the hull.
 */
export function gradedZAt(
  tin: TopoTin,
  live: ResolvedTopoModifier[],
  pt: Pt2,
): number | null {
  const z0 = tinZAt(tin, pt)
  if (z0 === null) return null
  const { retainLines, pads } = liveCaches(live)
  // FOOTPRINTS BEAT SKIRTS (topo grading fix round): inside any live pad's
  // footprint that pad's elevation is authoritative over every OTHER modifier's
  // skirt, regardless of stack order — an upper pad's 2:1 daylight run can no
  // longer bury an adjacent lower slab (the Forsyth garage repro). Stack order
  // still resolves pad-vs-pad footprint OVERLAPS, unchanged: the LAST containing
  // pad wins, exactly the precedence the old in-loop assignment produced.
  let containing: ResolvedTopoModifier | null = null
  for (const p of pads) {
    const [minX, minY, maxX, maxY] = p.bbox
    if (pt[0] < minX || pt[0] > maxX || pt[1] < minY || pt[1] > maxY) continue
    if (pointInRing(pt, p.r.poly!)) containing = p.r
  }
  if (containing) return padZAt(containing, pt)
  // PAVING HEAL: just outside a paving run the ground meets the slab's TOP edge (the seam
  // machinery samples here, so the terrain's boundary vertices land flush with the paving)
  for (const p of pads) {
    if (!p.r.edgeFn) continue
    const [minX, minY, maxX, maxY] = p.bbox
    if (pt[0] < minX - PAVING_EDGE_BAND || pt[0] > maxX + PAVING_EDGE_BAND || pt[1] < minY - PAVING_EDGE_BAND || pt[1] > maxY + PAVING_EDGE_BAND) continue
    if (distToRing(pt, p.r.poly!).d <= PAVING_EDGE_BAND) return p.r.edgeFn(pt)
  }
  let z = z0
  for (const r of live) {
    // each grading kind reduces to (distance d to its geometry, nearest point at,
    // target elevation there) — then one shared daylight meet applies. Total by
    // construction for every kind; stack order is the precedence.
    let d: number, at: Pt2, target: number
    if (r.mod.kind === 'pad' && r.poly && r.elevFt !== null && !r.isReferencing) {
      // pt is outside every live pad footprint here (the containment pass above
      // returned already otherwise) — only the skirt remains. REFERENCING pads
      // (isReferencing) fall through to the `else` below and contribute nothing
      // outside their own footprint — terrain grading simplification round: "grade
      // to this slab" goes vertical at the boundary, no daylight skirt.
      const hit = distToRing(pt, r.poly)
      d = hit.d; at = hit.at; target = padZAt(r, at)
    } else if (r.mod.kind === 'grade' && r.pt && r.elevFt !== null) {
      d = Math.hypot(pt[0] - r.pt[0], pt[1] - r.pt[1])
      at = r.pt; target = r.elevFt
    } else if (r.mod.kind === 'gradeLine' && r.poly && r.poly.length >= 2 && (r.zs || r.elevFt !== null)) {
      const hit = distToPolyline(pt, r.poly, r.zs)
      d = hit.d; at = hit.at
      target = hit.val ?? r.elevFt!
    } else {
      continue
    }
    // a retaining line between the point and the grading geometry blocks the skirt —
    // the surface drops vertically at the wall instead of daylighting past it.
    // Two stable conditions (Matt's report: "retaining edge doesn't seem to work on
    // walls"; the standard scenario puts the wall exactly AT the slab/pad edge,
    // where the skirt segment ENDS ON the wall line and a strict crossing test
    // never fired): the blocker strictly crosses pt→at, OR the blocker passes
    // within RETAIN_REACH_FT of `at` itself — which also covers a wall whose
    // stored location line sits a few inches inside the slab edge, without the
    // corner-flapping the old overshoot segment had (see RETAIN_REACH_FT's note).
    // Every OTHER pad's footprint ring blocks the same way (footprints beat
    // skirts): a skirt never re-emerges past a pad it can't cross.
    let blocked = false
    const segMinX = Math.min(pt[0], at[0]) - RETAIN_REACH_FT, segMaxX = Math.max(pt[0], at[0]) + RETAIN_REACH_FT
    const segMinY = Math.min(pt[1], at[1]) - RETAIN_REACH_FT, segMaxY = Math.max(pt[1], at[1]) + RETAIN_REACH_FT
    for (const line of retainLines) {
      for (let i = 0; i + 1 < line.length && !blocked; i++) {
        if (segsIntersect(pt, at, line[i], line[i + 1])
          || segDistToPoint(line[i], line[i + 1], at) <= RETAIN_REACH_FT) blocked = true
      }
      if (blocked) break
    }
    for (const p of pads) {
      if (blocked) break
      if (p.r === r) continue
      const [minX, minY, maxX, maxY] = p.bbox
      if (segMaxX < minX || segMinX > maxX || segMaxY < minY || segMinY > maxY) continue
      const ring = p.r.poly!
      for (let i = 0, j = ring.length - 1; i < ring.length && !blocked; j = i++) {
        if (segsIntersect(pt, at, ring[j], ring[i])
          || segDistToPoint(ring[j], ring[i], at) <= RETAIN_REACH_FT) blocked = true
      }
    }
    if (blocked) continue
    const rise = d / r.slopeH
    // tie back toward the current surface at the daylight slope until they meet
    z = z >= target ? Math.min(z, target + rise) : Math.max(z, target - rise)
  }
  return z
}

/**
 * The surface a view sees (TERRAIN LOCAL SURGERY round, 2026-08-15 —
 * docs/MODELER.md; Matt's conceptual model adopted verbatim):
 *
 * - No live modifier → the existing TIN, unchanged.
 * - Only REFERENCING pads (graded-to slabs) and regions live → LOCAL SURGERY on
 *   the existing TIN: region boundaries become true mesh edges (a z-preserving
 *   split, so 3D drape buckets align to the seams exactly), then each
 *   referencing pad projects its boundary, splits the crossed facets, deletes
 *   the void, and stitches a vertical band + flat cap (`padSurgery`). Every
 *   triangle outside the surgery is BYTE-IDENTICAL to the existing TIN — the
 *   invariant that makes the existing/proposed contour overlay coincide exactly
 *   where nothing changed, and gives crisp vertical faces on sparse surveys
 *   (no Delaunay to misbehave).
 * - Any DRAWN grading live (pads/grade points/grade lines — intentional
 *   daylight skirts) → the recorded global path: a fresh Delaunay over existing
 *   vertices + densified drawn-modifier boundaries + PER-MODIFIER bounded sample
 *   grids, every z through gradedZAt (skirted feathering is the by-hand case;
 *   its re-triangulation is inherent — the ground genuinely changes across the
 *   skirt reach). Referencing pads STILL get true-edge surgery on that base
 *   (chain z re-read just outside the ring — see padSurgery), so an existing
 *   graded-to slab keeps its clean face even while new drawn grading is live
 *   elsewhere. Retaining lines swap the old ±0.05' paired-offset-row
 *   approximation for the same true-edge surgery (a constrained line instead of
 *   a loop — `retainingSurgery`).
 *
 * `geometryFrom` (the contour-overlay coincidence contract on the global path):
 * the modifier set that generates the BASE GEOMETRY (densified boundaries +
 * sample grids), defaulting to `live`. The overlay's dashed baseline passes the
 * SOLID build's set here while evaluating z through only the earlier-phase
 * modifiers — SAME vertices, SAME base triangulation, different surface — so
 * dashed and solid coincide BIT-FOR-BIT wherever this phase's grading doesn't
 * move ground. Independently triangulating the two can never guarantee that on
 * a sparse survey: Delaunay's empty-circumcircle property is only as local as
 * the circumcircles, and a 50-point site has circumcircles spanning half the
 * lot (the Forsyth dashed/solid blanket, root-caused). gradedZAt is a total
 * function, so extra vertices from `geometryFrom`'s new-phase modifiers simply
 * land exactly ON the earlier-phase surface.
 */
export function buildGradedTin(
  tin: TopoTin,
  live: ResolvedTopoModifier[],
  geometryFrom: ResolvedTopoModifier[] = live,
  // TERRAIN FOUNDATION CUT round: foundation prisms join the SAME single seam
  // pass — cutting the already-seamed graded mesh in a second surgery aliased
  // the pad seams' duplicated vertices exactly like the original tear bug (a
  // stem wall's ring runs ON its slab's pad boundary). All splits before any
  // duplication, always. 3D-solid callers only; 2D never passes cutters.
  cutters: TerrainCutter[] = [],
): TopoTin {
  // content cache (terrain perf round): keyed on the existing tin's IDENTITY
  // (buildExistingTin returns the same object for the same survey) + the
  // resolved geometry of the modifier sets and cutters. The plan's solid +
  // dashed variants, the 3D solid, and the cut rebuild all land here — an edit
  // that changes none of these inputs pays nothing.
  let byKey = GRADED_CACHE.get(tin)
  if (!byKey) { byKey = new Lru<TopoTin>(8); GRADED_CACHE.set(tin, byKey) }
  const key = `${fpModifiers(live)}|${geometryFrom === live ? '=' : fpModifiers(geometryFrom)}|${fpCutters(cutters)}`
  const hit = byKey.get(key)
  if (hit) return hit
  const built = buildGradedTinImpl(tin, live, geometryFrom, cutters)
  byKey.set(key, built)
  return built
}

function buildGradedTinImpl(
  tin: TopoTin,
  live: ResolvedTopoModifier[],
  geometryFrom: ResolvedTopoModifier[],
  cutters: TerrainCutter[],
): TopoTin {
  const refPadsOf = (set: ResolvedTopoModifier[]) => set.filter((r) =>
    r.mod.kind === 'pad' && r.isReferencing && r.poly && r.poly.length >= 3 && r.elevFt !== null)
  const drawnOf = (set: ResolvedTopoModifier[]) => set.filter((r) =>
    (r.mod.kind === 'pad' && !r.isReferencing && r.poly && r.poly.length >= 3 && r.elevFt !== null)
    || (r.mod.kind === 'grade' && r.pt && r.elevFt !== null)
    || (r.mod.kind === 'gradeLine' && r.poly && r.poly.length >= 2 && (r.zs || r.elevFt !== null)))
  const regionsOf = (set: ResolvedTopoModifier[]) =>
    set.filter((r) => r.mod.kind === 'region' && r.poly && r.poly.length >= 3)

  const geomDrawn = drawnOf(geometryFrom)
  const geomRegions = regionsOf(geometryFrom)
  const geomRefPads = refPadsOf(geometryFrom)
  const zRefPads = refPadsOf(live)   // z + interiors follow the Z set

  // live cutters: pruned HARD against the graded surface FUNCTION before any
  // splitting — a real model carries hundreds of wall/stem/footing prisms and
  // nearly all sit fully above the surface (walls on slabs) or fully buried;
  // only a prism whose z-range straddles the local surface can change anything.
  // Sampled at ring vertices + edge midpoints (foundation footprints are thin
  // strips; an interior-only dip slipping the samples is recorded acceptable).
  const surfaceFn = (pt: Pt2): number | null => gradedZAt(tin, live, pt)
  const liveCutters = cutters
    .map((c) => ({ ...c, rings: c.rings.filter((r) => r.length >= 3) }))
    .filter((c) => c.rings.length && c.zTop > c.zBot + 1e-6)
    .filter((c) => {
      for (const r of c.rings) {
        for (let i = 0; i < r.length; i++) {
          const a = r[i], b = r[(i + 1) % r.length]
          for (const p of [a, [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2] as Pt2]) {
            const z = surfaceFn(p)
            if (z !== null && z > c.zBot + 1e-6 && (c.unroofed || z <= c.zTop + 1e-6)) return true
          }
        }
      }
      return false
    })
  // XOR containment across a cutter's rings — clipper hole rings subtract
  const inCutter = (pt: Pt2, c: { rings: Pt2[][] }): boolean => {
    let inside = false
    for (const r of c.rings) if (pointInRing(pt, r)) inside = !inside
    return inside
  }
  /** the conditional boolean: earth drops to a cutter's bottom only where the
   *  surface lies within that cutter's own z-range (buried stays buried) */
  const cutZ = (pt: Pt2, z0: number | null): number | null => {
    if (z0 === null) return null
    let z = z0
    for (const c of liveCutters) {
      if (z0 > c.zBot + 1e-6 && (c.unroofed || z0 <= c.zTop + 1e-6) && inCutter(pt, c)) {
        z = Math.min(z, c.zBot)
      }
    }
    return z
  }

  // seam paths: geometry comes from `geometryFrom` so the dashed and solid
  // builds split identically; z (and therefore which seams actually open into
  // vertical faces) comes from `live` via resolveSeams' sampling. Pad rings
  // come FIRST — they are the authoritative geometry the canonicalization
  // below snaps retaining lines (and foundation cutter rings) onto.
  const seamPaths: { pts: Pt2[]; closed: boolean }[] = []
  const collectSeams = (withRetaining: boolean) => {
    for (const r of geomRefPads) seamPaths.push({ pts: r.poly!, closed: true })
    if (withRetaining) {
      for (const r of geometryFrom) {
        if (r.mod.kind === 'retaining' && r.line && r.line.length >= 2) {
          seamPaths.push({ pts: r.line, closed: false })
        }
      }
    }
    for (const c of liveCutters) for (const r of c.rings) seamPaths.push({ pts: r, closed: true })
  }

  let base: TopoTin
  // region boundaries are z-preserving splits (3D drape seams) — they join the
  // same batched split, their chains simply aren't seams
  const regionPaths: { pts: Pt2[]; closed: boolean }[] = []
  if (!geomDrawn.length) {
    // the byte-identity path — pure local surgery on the existing TIN.
    // Retaining lines are inert here (no skirts to block → no discontinuity),
    // so they are not split — untouched facets stay byte-identical.
    if (!geomRefPads.length && !geomRegions.length && !liveCutters.length) return tin
    base = tin
    for (const r of geomRegions) regionPaths.push({ pts: r.poly!, closed: true })
    collectSeams(false)
  } else {
    base = retriangulatedBase(tin, live, geomDrawn, geomRegions)
    collectSeams(true)
  }
  if (!seamPaths.length && !regionPaths.length) return base

  // NEAR-COINCIDENT FEATURES ARE THE SAME LINE BY INTENT: a retaining wall
  // drawn on a slab edge (or two slabs sharing an edge line) can sit a few
  // hundredths of a foot apart in the stored data. Splitting them as separate
  // seams sheds micro-slivers and ill-conditioned shallow-angle intersections
  // (the Forsyth tears). Canonicalize: each seam path's vertices snap onto
  // already-accepted seam vertices, then segments, within PATH_SNAP — pad rings
  // were pushed first, so walls snap onto slab edges, not vice versa.
  const PATH_SNAP = 0.05
  const accPts: Pt2[] = []
  const accSegs: [Pt2, Pt2][] = []
  const canonPaths = seamPaths.map(({ pts, closed }) => {
    const snapped: Pt2[] = pts.map((p) => {
      let best: Pt2 | null = null
      let bestD = PATH_SNAP
      for (const q of accPts) {
        const d = Math.hypot(p[0] - q[0], p[1] - q[1])
        if (d <= bestD) { best = [q[0], q[1]]; bestD = d }
      }
      if (!best) {
        for (const [a, b] of accSegs) {
          const dx = b[0] - a[0], dy = b[1] - a[1]
          const len2 = dx * dx + dy * dy
          if (len2 < 1e-12) continue
          const t = Math.max(0, Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / len2))
          const px = a[0] + dx * t, py = a[1] + dy * t
          const d = Math.hypot(p[0] - px, p[1] - py)
          if (d <= bestD) { best = [px, py]; bestD = d }
        }
      }
      return best ?? p
    })
    for (const p of snapped) accPts.push(p)
    const segEnd = closed ? snapped.length : snapped.length - 1
    for (let i = 0; i < segEnd; i++) accSegs.push([snapped[i], snapped[(i + 1) % snapped.length]])
    return { pts: snapped, closed }
  })

  // TEAR FIX (same-day follow-up, Matt's Forsyth screenshot): ALL splits happen
  // before ANY duplication — sequential per-modifier surgeries aliased each
  // other's same-xy seam copies wherever features coincide (walls on slab
  // edges, slabs sharing edge lines), tearing the mesh open. Every canonicalized
  // path splits in ONE BATCHED call (terrain perf round — shared setup tables,
  // one vertex registry, path×path crossings precomputed as shared events), so
  // chains reference the final mesh exactly; resolveSeams' expandPair remains as
  // a safety net for residual float paths. See surgery.ts's module header.
  // Splits run SEQUENTIALLY, one path per call, on purpose: batching them all
  // into one multi-path call was measured FASTER but leaked ~5× more open edges
  // on the live Forsyth model (216 vs 42 — dozens of interacting rings through
  // the multi-chain per-facet retriangulation), and the perf round's other two
  // levers (the content-keyed tin cache + the pipeline's terrain-debt defer)
  // already take every rebuild off the interactive path — so tear quality wins.
  // The batched engine (splitTinAlongPaths) remains the one implementation;
  // single-path calls route through it.
  const seamEdges: [number, number][] = []
  const takeChain = (chain: number[]) => {
    for (let i = 0; i + 1 < chain.length; i++) {
      if (chain[i] >= 0 && chain[i + 1] >= 0) seamEdges.push([chain[i], chain[i + 1]])
    }
  }
  for (const p of regionPaths) base = splitTinAlongPath(base, p.pts, p.closed).tin
  for (const p of canonPaths) {
    const res = splitTinAlongPath(base, p.pts, p.closed)
    base = res.tin
    takeChain(res.chain)
  }
  if (!seamEdges.length) return base
  const seamVerts = new Set<number>()
  for (const [a, b] of seamEdges) { seamVerts.add(a); seamVerts.add(b) }

  // pad interiors re-elevate IN PLACE (the cap is the original facets at the
  // pad elevation; no delete/re-add) — last containing pad wins, matching
  // gradedZAt's own stack rule — then the foundation cutters' conditional
  // applies over the result. Seam vertices are resolved per-side instead.
  if (zRefPads.length || liveCutters.length) {
    const zs = base.zs === tin.zs ? tin.zs.slice() : base.zs
    for (let v = 0; v < base.pts.length; v++) {
      if (seamVerts.has(v)) continue
      let elev: number | null = null
      for (const r of zRefPads) {
        if (pointInRing(base.pts[v], r.poly!)) elev = padZAt(r, base.pts[v])
      }
      let z = elev ?? zs[v]
      if (liveCutters.length) z = cutZ(base.pts[v], z) ?? z
      if (z !== zs[v]) zs[v] = z
    }
    if (zs !== base.zs) base = { ...base, zs }
  }

  return resolveSeams(base, seamEdges, (pt) => cutZ(pt, surfaceFn(pt)))
}

// ---------- foundation cuts (TERRAIN FOUNDATION CUT round, 2026-08-15) ----------

/** a prism to boolean OUT of the terrain: a solid's footprint RING SET + z-range
 *  (wall bodies, stem walls, footings — gathered from built solids). Containment
 *  is XOR across the rings, so a closed-loop wall's annulus footprint (outer ring
 *  + courtyard hole ring) cuts the wall band only — never the courtyard.
 *
 *  `unroofed` (wall/stem bodies): cut whenever the surface is above the BASE,
 *  even when it stands above the prism's top — a wall whose top sits below
 *  grade is a retaining/stem condition and the open slot is the honest read
 *  (Matt's courtyard report: walls swallowed whole by the hillside weren't
 *  carving at all). Footings stay windowed — a buried footing must never trench
 *  the ground above it. */
export interface TerrainCutter {
  rings: Pt2[][]
  zBot: number
  zTop: number
  unroofed?: boolean
}


/**
 * The recorded global path for DRAWN grading (skirted feathering): a fresh
 * Delaunay over existing vertices + densified drawn-modifier boundaries + a
 * bounded sample grid across the affected area, every z evaluated through
 * gradedZAt. Referencing pads and retaining lines are handled by surgery
 * afterward and contribute nothing here.
 */
function retriangulatedBase(
  tin: TopoTin,
  live: ResolvedTopoModifier[],
  drawn: ResolvedTopoModifier[],
  regions: ResolvedTopoModifier[],
): TopoTin {
  const affecting = [...drawn, ...regions]
  const pts: Pt2[] = tin.pts.map((p) => [p[0], p[1]])
  const seen = new Set(pts.map((p) => `${Math.round(p[0] * 1e4)},${Math.round(p[1] * 1e4)}`))
  const push = (x: number, y: number) => {
    const key = `${Math.round(x * 1e4)},${Math.round(y * 1e4)}`
    if (seen.has(key)) return
    seen.add(key)
    pts.push([x, y])
  }
  // densified modifier geometry — pad creases, region seams, grade-line inverts all
  // need triangulation edges along them; a grade point needs its apex vertex.
  // Points OUTSIDE the existing hull are skipped (review fix: they have no surface z
  // and would have landed at a literal 0 — geometry poking past the surveyed extent
  // simply shapes only where terrain exists).
  //
  // TERRAIN LOCAL SURGERY round: every vertex this base adds is PER-MODIFIER
  // deterministic — each modifier's densified boundary, paired rows, and sample
  // grid depend only on that ONE modifier (its own bbox + its own reach + the
  // fixed tin z-range), never on which OTHER modifiers happen to be live. That is
  // what makes the phase-relative contour overlay honest on this path too: the
  // dashed build (earlier-phase modifiers) and the solid build (plus this phase's)
  // generate IDENTICAL vertices everywhere except near the new modifiers, so
  // Delaunay — local by the empty-circumcircle property — coincides away from the
  // new work. The old single union-bbox grid re-gridded the WHOLE affected area
  // whenever any modifier joined, which is exactly what blanketed Forsyth's New
  // Construction plan in dashed/solid pairs.
  let zMin = Infinity, zMax = -Infinity
  for (const z of tin.zs) { if (z < zMin) zMin = z; if (z > zMax) zMax = z }
  // skirt reach bound for a grading target: worst terrain-to-target drop × slope
  const reachOf = (target: number, slopeH: number) =>
    Math.max(Math.abs(zMax - target), Math.abs(zMin - target)) * slopeH
  // one bounded sample grid PER modifier over its own bbox (+ its own skirt
  // reach) — smooth daylight lines without touching vertices elsewhere. `seen`
  // dedupes overlaps between modifiers' grids. <= (not <): a LONE grade point has
  // a zero-area bbox before padding, which would otherwise skip the grid.
  const pushGrid = (minX: number, minY: number, maxX: number, maxY: number, drop: number) => {
    if (!(minX <= maxX && minY <= maxY)) return
    const pad = Math.min(200, drop + DENSIFY_FT)
    minX -= pad; minY -= pad; maxX += pad; maxY += pad
    const step = Math.max(2, Math.min(10, Math.max(maxX - minX, maxY - minY) / 40))
    for (let x = minX; x <= maxX; x += step) {
      for (let y = minY; y <= maxY; y += step) {
        if (tinZAt(tin, [x, y]) !== null) push(x, y)
      }
    }
  }
  for (const r of affecting) {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
    let drop = 0
    const growPt = (x: number, y: number) => {
      if (x < minX) minX = x
      if (x > maxX) maxX = x
      if (y < minY) minY = y
      if (y > maxY) maxY = y
    }
    if (r.mod.kind === 'grade') {
      const [x, y] = r.pt!
      if (tinZAt(tin, [x, y]) !== null) push(x, y)
      growPt(x, y)
      pushGrid(minX, minY, maxX, maxY, reachOf(r.elevFt!, r.slopeH))
      continue
    }
    const poly = r.poly!
    // gradeLine is an OPEN polyline — no closing edge to densify
    const segCount = r.mod.kind === 'gradeLine' ? poly.length - 1 : poly.length
    for (let s = 0; s < segCount; s++) {
      const a = poly[r.mod.kind === 'gradeLine' ? s : (s + poly.length - 1) % poly.length]
      const b = poly[r.mod.kind === 'gradeLine' ? s + 1 : s]
      const len = Math.hypot(b[0] - a[0], b[1] - a[1])
      const n = Math.max(1, Math.min(32, Math.round(len / DENSIFY_FT)))
      // open polylines include the far endpoint (k <= n); rings skip it (the next
      // segment starts there)
      const kEnd = r.mod.kind === 'gradeLine' && s === segCount - 1 ? n : n - 1
      for (let k = 0; k <= kEnd; k++) {
        const t = k / n
        const x = a[0] + (b[0] - a[0]) * t
        const y = a[1] + (b[1] - a[1]) * t
        if (tinZAt(tin, [x, y]) !== null) push(x, y)
      }
    }
    for (const [x, y] of poly) growPt(x, y)
    if (r.mod.kind === 'pad' && r.elevFt !== null) {
      // drawn pads only reach here (TERRAIN LOCAL SURGERY round — referencing
      // pads get true-edge surgery after this base instead; the old spiky-fan
      // grid-padding workaround for their cliffs is obsolete, exact edges need
      // no sample density)
      drop = reachOf(r.elevFt, r.slopeH)
    } else if (r.mod.kind === 'gradeLine') {
      const targets = r.zs ?? [r.elevFt!]
      for (const t of targets) drop = Math.max(drop, reachOf(t, r.slopeH))
    }
    pushGrid(minX, minY, maxX, maxY, drop)
  }
  // DRAWN pad footprint rings get paired offset rows (±0.05') so a vertical step
  // gets its own hugging triangles — footprints beat skirts, so a pad boundary is
  // a potential vertical face wherever another modifier's skirt terminates against
  // it; two adjacent graded slabs read slab / cliff / slab. (RETAINING lines used
  // to get the same rows — TERRAIN LOCAL SURGERY round replaced that approximation
  // with true-edge surgery, `retainingSurgery`, applied on this base afterward.
  // Referencing pads likewise never reach here anymore.)
  const STEP_EPS = 0.05
  const pushPairedRows = (line: Pt2[], closed: boolean) => {
    const segEnd = closed ? line.length : line.length - 1
    for (let s = 0; s < segEnd; s++) {
      const a = line[s], b = line[(s + 1) % line.length]
      const len = Math.hypot(b[0] - a[0], b[1] - a[1])
      if (len < 1e-9) continue
      const nx = -(b[1] - a[1]) / len, ny = (b[0] - a[0]) / len
      const n = Math.max(1, Math.min(32, Math.round(len / DENSIFY_FT)))
      for (let k = 0; k <= n; k++) {
        const t = k / n
        const x = a[0] + (b[0] - a[0]) * t
        const y = a[1] + (b[1] - a[1]) * t
        for (const side of [1, -1]) {
          const px = x + nx * STEP_EPS * side
          const py = y + ny * STEP_EPS * side
          if (tinZAt(tin, [px, py]) !== null) push(px, py)
        }
      }
    }
  }
  for (const r of drawn) {
    if (r.mod.kind === 'pad' && r.poly && r.poly.length >= 3 && r.elevFt !== null) pushPairedRows(r.poly, true)
  }
  const zs = pts.map((p, i) => {
    const z = gradedZAt(tin, live, p)
    return z ?? (i < tin.zs.length ? tin.zs[i] : 0)
  })
  const del = Delaunator.from(pts, (p: Pt2) => p[0], (p: Pt2) => p[1])
  return { pts, zs, tris: Array.from(del.triangles), hull: Array.from(del.hull) }
}

// ---------- contours ----------

export interface TopoContour {
  zFt: number
  major: boolean
  /** chained polylines at this elevation */
  lines: Pt2[][]
}

/** TIN triangle × z-plane crossing segments at a single elevation. */
function triangleZSegments(tin: TopoTin, z: number): [Pt2, Pt2][] {
  const segs: [Pt2, Pt2][] = []
  for (let i = 0; i + 2 < tin.tris.length; i += 3) {
    const ids = [tin.tris[i], tin.tris[i + 1], tin.tris[i + 2]]
    const cross: Pt2[] = []
    for (let e = 0; e < 3; e++) {
      const a = ids[e], b = ids[(e + 1) % 3]
      const za = tin.zs[a], zb = tin.zs[b]
      if ((za < z) === (zb < z)) continue
      const t = (z - za) / (zb - za)
      if (!(t >= 0 && t <= 1)) continue
      cross.push([
        tin.pts[a][0] + (tin.pts[b][0] - tin.pts[a][0]) * t,
        tin.pts[a][1] + (tin.pts[b][1] - tin.pts[a][1]) * t,
      ])
    }
    if (cross.length === 2 && Math.hypot(cross[1][0] - cross[0][0], cross[1][1] - cross[0][1]) > 1e-6) {
      segs.push([cross[0], cross[1]])
    }
  }
  return segs
}

/** chain crossing segments end-to-end by endpoint hashing into polylines */
function chainContourSegs(segs: [Pt2, Pt2][]): Pt2[][] {
  const key = (p: Pt2) => `${Math.round(p[0] * 1e3)},${Math.round(p[1] * 1e3)}`
  const byEnd = new Map<string, number[]>()
  segs.forEach((s, i) => {
    for (const p of s) {
      const k = key(p)
      const list = byEnd.get(k)
      if (list) list.push(i)
      else byEnd.set(k, [i])
    }
  })
  const used = new Array(segs.length).fill(false)
  const lines: Pt2[][] = []
  for (let i = 0; i < segs.length; i++) {
    if (used[i]) continue
    used[i] = true
    const line: Pt2[] = [segs[i][0], segs[i][1]]
    for (const dir of [1, -1] as const) {
      for (;;) {
        const tip = dir === 1 ? line[line.length - 1] : line[0]
        const candidates = byEnd.get(key(tip)) ?? []
        const next = candidates.find((ci) => !used[ci])
        if (next === undefined) break
        used[next] = true
        const [a, b] = segs[next]
        const add = key(a) === key(tip) ? b : a
        if (dir === 1) line.push(add)
        else line.unshift(add)
      }
    }
    if (line.length >= 2) lines.push(line)
  }
  return lines
}

/**
 * Chained contour polylines at a SINGLE elevation — the shared extraction
 * `deriveContours` loops over per minor/major step, and the TERRAIN PLAN CUT round
 * reuses directly for the boundary at an arbitrary cutZ (docs/MODELER.md): the cut
 * line IS a contour, just at a z that isn't necessarily a minor/major multiple.
 */
export function contourLinesAtZ(tin: TopoTin, z: number): Pt2[][] {
  const segs = triangleZSegments(tin, z)
  return segs.length ? chainContourSegs(segs) : []
}

/**
 * Derived contours: TIN triangle × z-plane intersections chained into polylines,
 * minor/major classified by the two intervals (major heavier per the weights table;
 * labels derive from the major set). Pure derivation — nothing stored.
 *
 * `alignOffsetFt` (real-world minus project-relative, i.e. the site datum's own
 * offset — 0 when the caller has none) PHASE-SHIFTS the sampling grid so contours
 * land on whole numbers in REAL-WORLD terms, the civil-survey convention (Matt's
 * report, 2026-08-20: contours read 84.14'/89.14'/94.14' instead of 84'/89'/94' once
 * a fractional site-datum offset existed — they were snapping to round PROJECT
 * elevations and only converting to real-world for the label, so any non-integer
 * offset carried straight through as drift). The TIN itself never changes — this
 * only chooses WHICH z's to slice at, still expressed in the tin's own
 * project-relative frame; `z + alignOffsetFt` is what lands on the round number.
 */
export function deriveContours(tin: TopoTin, minorFt: number, majorFt: number, alignOffsetFt = 0): TopoContour[] {
  if (!(minorFt > 0)) return []
  let zMin = Infinity, zMax = -Infinity
  for (const z of tin.zs) { if (z < zMin) zMin = z; if (z > zMax) zMax = z }
  if (!(zMax > zMin)) return []
  const out: TopoContour[] = []
  const start = Math.ceil((zMin + alignOffsetFt) / minorFt) * minorFt - alignOffsetFt
  for (let z = start; z <= zMax + 1e-9; z += minorFt) {
    const lines = contourLinesAtZ(tin, z)
    if (!lines.length) continue
    const real = z + alignOffsetFt
    const major = majorFt > 0 && Math.abs(real / majorFt - Math.round(real / majorFt)) < 1e-6
    out.push({ zFt: Math.round(z * 1e6) / 1e6, major, lines })
  }
  return out
}

/**
 * Sutherland-Hodgman clip of one triangle against the half-plane z >= z0 — the
 * ABOVE-cut fragment, 0/3/4 vertices. Shares the crossing-edge math with
 * `triangleZSegments` above (same idea: an edge whose endpoints straddle z lerps to
 * the crossing point) but keeps the vertices instead of just the two intersections.
 */
function clipTriAboveZ(
  pa: Pt2, pb: Pt2, pc: Pt2, za: number, zb: number, zc: number, z: number,
): Pt2[] | null {
  const verts: [Pt2, number][] = [[pa, za], [pb, zb], [pc, zc]]
  const out: Pt2[] = []
  for (let i = 0; i < 3; i++) {
    const [p0, z0] = verts[i]
    const [p1, z1] = verts[(i + 1) % 3]
    const in0 = z0 >= z, in1 = z1 >= z
    if (in0) out.push(p0)
    if (in0 !== in1) {
      const t = (z - z0) / (z1 - z0)
      out.push([p0[0] + (p1[0] - p0[0]) * t, p0[1] + (p1[1] - p0[1]) * t])
    }
  }
  return out.length >= 3 ? out : null
}

/**
 * TERRAIN PLAN CUT round (docs/MODELER.md): the region of the TIN at/above
 * elevation z, as per-triangle clipped fragments — no boolean union. The fragments
 * abut edge-to-edge exactly like the triangulation they came from (Delaunay is a
 * partition of the hull), so one fill entry drawing every fragment as its own ring
 * paints the whole region seamlessly.
 */
export function tinAboveZFragments(tin: TopoTin, z: number): Pt2[][] {
  const out: Pt2[][] = []
  for (let i = 0; i + 2 < tin.tris.length; i += 3) {
    const ia = tin.tris[i], ib = tin.tris[i + 1], ic = tin.tris[i + 2]
    const frag = clipTriAboveZ(
      tin.pts[ia], tin.pts[ib], tin.pts[ic],
      tin.zs[ia], tin.zs[ib], tin.zs[ic], z,
    )
    if (frag) out.push(frag)
  }
  return out
}

// ---------- regions, areas, cut/fill ----------

/**
 * Per-material draped plan areas of the live region modifiers, later regions winning
 * overlaps (stack order — the same precedence rule as grading). Sampled, not
 * boolean'd: total by construction.
 */
export function regionAreas(
  tin: TopoTin,
  live: ResolvedTopoModifier[],
): Map<string, number> {
  const regions = live.filter((r) => r.mod.kind === 'region' && r.poly && r.mod.materialId)
  const out = new Map<string, number>()
  if (!regions.length) return out
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
  for (const r of regions) {
    for (const [x, y] of r.poly!) {
      if (x < minX) minX = x
      if (x > maxX) maxX = x
      if (y < minY) minY = y
      if (y > maxY) maxY = y
    }
  }
  const step = Math.max(0.5, Math.max(maxX - minX, maxY - minY) / 200)
  const cell = step * step
  for (let x = minX + step / 2; x < maxX; x += step) {
    for (let y = minY + step / 2; y < maxY; y += step) {
      const pt: Pt2 = [x, y]
      if (tinZAt(tin, pt) === null) continue
      // LAST containing region wins — stack order
      let winner: string | null = null
      for (const r of regions) {
        if (pointInRing(pt, r.poly!)) winner = r.mod.materialId!
      }
      if (winner) out.set(winner, (out.get(winner) ?? 0) + cell)
    }
  }
  return out
}

/** The last live region containing a point, or null (the 3D drape + plan hatch rule). */
export function regionMaterialAt(live: ResolvedTopoModifier[], pt: Pt2): string | null {
  let winner: string | null = null
  for (const r of live) {
    if (r.mod.kind === 'region' && r.poly && r.mod.materialId && pointInRing(pt, r.poly)) {
      winner = r.mod.materialId
    }
  }
  return winner
}

/**
 * CUT/FILL — the derivable diff between the existing surface and the graded one,
 * sampled across the modified area. Cubic feet (callers divide by 27 for yards).
 */
export function cutFill(
  tin: TopoTin,
  live: ResolvedTopoModifier[],
): { cutCuFt: number; fillCuFt: number } {
  // every GRADING kind moves earth — pads, grade points, grade lines (regions are
  // material-only, retaining edges only shape the others' skirts)
  const graders = live.filter((r) =>
    (r.mod.kind === 'pad' && r.poly && r.elevFt !== null)
    || (r.mod.kind === 'grade' && r.pt && r.elevFt !== null)
    || (r.mod.kind === 'gradeLine' && r.poly && r.poly.length >= 2 && (r.zs || r.elevFt !== null)))
  if (!graders.length) return { cutCuFt: 0, fillCuFt: 0 }
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
  let maxReach = 0
  let zMin = Infinity, zMax = -Infinity
  for (const z of tin.zs) { if (z < zMin) zMin = z; if (z > zMax) zMax = z }
  for (const r of graders) {
    for (const [x, y] of (r.poly ?? (r.pt ? [r.pt] : []))) {
      if (x < minX) minX = x
      if (x > maxX) maxX = x
      if (y < minY) minY = y
      if (y > maxY) maxY = y
    }
    const targets = r.mod.kind === 'gradeLine' ? (r.zs ?? [r.elevFt!]) : [r.elevFt!]
    for (const t of targets) {
      const dz = Math.max(Math.abs(zMax - t), Math.abs(zMin - t))
      maxReach = Math.max(maxReach, dz * r.slopeH)
    }
  }
  const pad = Math.min(200, maxReach + DENSIFY_FT)
  minX -= pad; minY -= pad; maxX += pad; maxY += pad
  const step = Math.max(0.5, Math.max(maxX - minX, maxY - minY) / 150)
  const cell = step * step
  let cut = 0, fill = 0
  for (let x = minX + step / 2; x < maxX; x += step) {
    for (let y = minY + step / 2; y < maxY; y += step) {
      const pt: Pt2 = [x, y]
      const z0 = tinZAt(tin, pt)
      if (z0 === null) continue
      const z1 = gradedZAt(tin, live, pt)
      if (z1 === null) continue
      const dz = z1 - z0
      if (dz > 0) fill += dz * cell
      else cut += -dz * cell
    }
  }
  return { cutCuFt: cut, fillCuFt: fill }
}
